import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { ROLE_PERMISSIONS, type UserRole, type Permission } from '@shared/permissions';
import { handle401Redirect, queryClient } from './queryClient';
import { prefetchForRole } from './routePrefetch';
import { preloadChunksForRole } from './chunkPreloader';
import { dismissAllToasts } from '@/hooks/use-toast';

export type { UserRole, Permission } from '@shared/permissions';

/**
 * Returns a stable device identifier for this browser, creating one if absent.
 * Stored in localStorage under "nashid:deviceId" so it persists across sessions
 * on the same browser. Used as the required `deviceId` field in login requests.
 */
function getOrCreateDeviceId(): string {
  const key = "nashid:deviceId";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, id);
    return id;
  } catch {
    // localStorage unavailable (private browsing, SSR, etc.) — return a session-scoped id
    return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export interface TenantFeatures {
  ptw: boolean;
  credentials: boolean;
  safety: boolean;
  equipment: boolean;
  timeAttendance: boolean;
  workforceReadiness: boolean;
  orchestrationEngine: boolean;
  evidencePacks: boolean;
  advancedAnalytics: boolean;
}

export interface TenantBranding {
  logoUrl: string | null;
  logoUrlDark: string | null;
  primaryColor: string | null;
  tagline: string | null;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  role: string;
  status: string;
}

export interface TenantMembershipInfo {
  tenantId: string;
  role: string;
  isPrimary: boolean;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  status: "active" | "inactive";
  joinedDate?: string;
  hasCompletedOnboarding?: boolean;
  activeTenant?: TenantInfo | null;
  tenantRole?: string | null;
  tenants?: TenantInfo[];
  permissions?: string[];
  /**
   * Fully-resolved effective permissions for this user in the current tenant
   * context, materialized by the 6-layer engine on /api/auth/me
   * (role → custom-role overrides → feature-flag stripping). This is the
   * canonical source used by hasPermission(); `permissions` is retained as a
   * back-compat alias.
   */
  resolvedPermissions?: Permission[];
  companyId?: string | null;
  linkedWorkerId?: string | null;
  linkedWorkerSiteIds?: string[];
  linkedWorkerAreaIds?: string[];
  linkedWorkerCrewId?: string | null;
  linkedWorkerDepartmentId?: string | null;
  linkedWorkerDivisionId?: string | null;
  linkedWorkerCompanyId?: string | null;
  /** Entity-level area scope grants. Non-empty means user is restricted to these areas/sites. */
  areaScopes?: Array<{ siteId: string; areaId: string }>;
  /** Project-role assignments from user_project_role_assignments. */
  projectRoles?: Array<{ projectId: string; ptwRoles: string[] }>;
  /** Company memberships expressed as scope entries for entity-level filtering. */
  companyScopes?: Array<{ companyId: string }>;
  /** Departments the user heads (linked worker is headWorkerId). */
  departmentScopes?: Array<{ departmentId: string; departmentName: string; companyId: string }>;
  /** Task #2327 — divisions where this user is lead */
  divisionScopes?: Array<{ divisionId: string; divisionName: string; departmentId: string }>;
  mfaEnabled?: boolean;
}

export interface RoleHistory {
  id: string;
  userId: string;
  userName: string;
  oldRole: UserRole;
  newRole: UserRole;
  changedBy: string;
  date: string;
}

export interface CompanyPortalInfo {
  tenantId: string;
  tenantName: string;
  companyId: string;
  companyName: string;
  participationRole: string;
}

interface ApiUserResponse {
  id: string;
  name: string;
  email: string | null;
  role: string;
  status: string;
  permissions?: string[];
  resolvedPermissions?: string[];
  hasCompletedOnboarding?: boolean;
  activeTenant?: TenantInfo | null;
  tenantRole?: string | null;
  tenants?: TenantInfo[];
  companyPortals?: CompanyPortalInfo[];
  tenantFeatures?: TenantFeatures | null;
  tenantBranding?: TenantBranding | null;
  companyId?: string | null;
  linkedWorkerId?: string | null;
  linkedWorkerSiteIds?: string[];
  linkedWorkerAreaIds?: string[];
  linkedWorkerCrewId?: string | null;
  linkedWorkerDepartmentId?: string | null;
  linkedWorkerDivisionId?: string | null;
  linkedWorkerCompanyId?: string | null;
  areaScopes?: Array<{ siteId: string; areaId: string }>;
  projectRoles?: Array<{ projectId: string; ptwRoles: string[] }>;
  companyScopes?: Array<{ companyId: string }>;
  departmentScopes?: Array<{ departmentId: string; departmentName: string; companyId: string }>;
  divisionScopes?: Array<{ divisionId: string; divisionName: string; departmentId: string }>;
  mfaEnabled?: boolean;
}

interface ApiErrorResponse {
  message: string;
  error?: {
    code?: string;
    message?: string;
    details?: { userId?: string };
  };
}

export interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * PTW-018: True while a warm-start (returning user with the nashid_sh hint)
   * still has its background /api/auth/me check in flight. During this window
   * `isLoading` is intentionally false (so the shell renders immediately) but
   * the session is not yet confirmed. AuthGate uses this to avoid bouncing a
   * deep link to /auth/login before the session resolves.
   */
  isVerifyingSession: boolean;
  /** True while checkSession is receiving 503 SERVER_STARTING responses — server is still booting. */
  isServerStarting: boolean;
  /** True while a tenant switch is in progress. */
  isSwitchingTenant: boolean;
  tenantUsers: User[];
  addUser: (user: Omit<User, "id" | "joinedDate" | "status"> & { password?: string }) => Promise<void>;
  updateUserRole: (userId: string, newRole: UserRole) => Promise<void>;
  removeUser: (userId: string) => Promise<void>;
  refreshUsers: () => Promise<void>;
  roleHistory: RoleHistory[];
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; role?: string; hasCompletedOnboarding?: boolean; tenants?: TenantInfo[]; mfaRequired?: boolean; partnerPortalRedirect?: boolean; partnerIsUnassigned?: boolean; requiresPasswordChange?: boolean; userId?: string }>;
  logout: () => Promise<void>;
  activeTenant: TenantInfo | null;
  tenants: TenantInfo[];
  companyPortals: CompanyPortalInfo[];
  tenantFeatures: TenantFeatures | null;
  tenantBranding: TenantBranding | null;
  switchTenant: (tenantId: string, companyId?: string) => Promise<void>;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Optimistic session hint ──────────────────────────────────────────────────
// Task #3408: read the warm-start hint synchronously at module scope so it is
// available before the first render. This lets AuthProvider initialise
// isLoading=false for returning users, avoiding the full-screen startup splash
// on every hard refresh.
const hasSessionHint =
  typeof localStorage !== "undefined" &&
  localStorage.getItem("nashid_sh") === "1";

async function authFetch(url: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...options,
    credentials: "include",
  });
  if (res.status === 401) {
    handle401Redirect(url);
  }
  return res;
}

function mapApiUser(data: ApiUserResponse): User {
  return {
    id: data.id,
    name: data.name,
    email: data.email || "",
    role: data.role as UserRole,
    status: (data.status as "active" | "inactive") || "active",
    hasCompletedOnboarding: data.hasCompletedOnboarding ?? true,
    activeTenant: data.activeTenant || null,
    tenantRole: data.tenantRole || null,
    tenants: data.tenants || [],
    permissions: data.permissions || [],
    resolvedPermissions: (data.resolvedPermissions || data.permissions || []) as Permission[],
    companyId: data.companyId ?? null,
    linkedWorkerId: data.linkedWorkerId ?? null,
    linkedWorkerSiteIds: data.linkedWorkerSiteIds ?? [],
    linkedWorkerAreaIds: data.linkedWorkerAreaIds ?? [],
    linkedWorkerCrewId: data.linkedWorkerCrewId ?? null,
    linkedWorkerDepartmentId: data.linkedWorkerDepartmentId ?? null,
    linkedWorkerDivisionId: data.linkedWorkerDivisionId ?? null,
    linkedWorkerCompanyId: data.linkedWorkerCompanyId ?? null,
    areaScopes: data.areaScopes ?? [],
    projectRoles: data.projectRoles ?? [],
    companyScopes: data.companyScopes ?? [],
    departmentScopes: data.departmentScopes ?? [],
    divisionScopes: data.divisionScopes ?? [],
    mfaEnabled: data.mfaEnabled ?? false,
  };
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  // Task #3408 Step 2: for returning users (hasSessionHint=true), start with
  // isLoading=false so the app shell renders immediately. The background check
  // still fires; if the session is expired it clears the user and redirects.
  const [isLoading, setIsLoading] = useState(hasSessionHint ? false : true);
  // PTW-018: track whether the background session check has resolved. For
  // warm-start users isLoading is false immediately, but until this flips true
  // the session is not yet confirmed — AuthGate must not treat a null user as
  // unauthenticated (which would discard a deep link and bounce to login).
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isServerStarting, setIsServerStarting] = useState(false);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const [roleHistory, setRoleHistory] = useState<RoleHistory[]>([]);
  const [activeTenant, setActiveTenant] = useState<TenantInfo | null>(null);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [companyPortals, setCompanyPortals] = useState<CompanyPortalInfo[]>([]);
  const [tenantFeatures, setTenantFeatures] = useState<TenantFeatures | null>(null);
  const [tenantBranding, setTenantBranding] = useState<TenantBranding | null>(null);

  // Guard: ensure the auth-ready / prefetch logic fires exactly once per session.
  const authReadyFiredRef = useRef(false);

  // GAP-024: Track whether the initial page-load had a warm-start hint, and
  // whether the session check subsequently found it expired. When a warm-start
  // session expires we redirect immediately before React can re-paint the app
  // shell, preventing a visible flash of the authenticated layout.
  const wasWarmStartRef = useRef(hasSessionHint);
  const sessionExpiredDuringWarmStartRef = useRef(false);

  // PTW-AUD-280: set when the SERVER_STARTING retry budget (≥90 s) is exhausted
  // while the server is still booting. The finally block reads this so it does
  // NOT flip the app out of the loading state — which would drop a perfectly
  // valid PTW session to the login screen during a slow post-deploy cold-wake.
  const serverStartTimedOutRef = useRef(false);

  // Task #3408 Step 1: coordinate HTML pre-loader dismissal with auth resolution.
  // When isLoading transitions to false, call window.__nashidAuthReady (registered
  // by RootErrorBoundary.componentDidMount) and kick off role-based prefetch.
  useEffect(() => {
    if (!isLoading && !authReadyFiredRef.current) {
      authReadyFiredRef.current = true;
      // Dismiss the HTML pre-loader via the registered callback.
      const fn = (window as any).__nashidAuthReady;
      if (typeof fn === "function") {
        fn();
      }
      (window as any).__nashidAuthReady = "done";
    }
  }, [isLoading]);

  // Kick off role-based prefetch after auth resolves.
  useEffect(() => {
    if (!isLoading && currentUser?.role) {
      try { prefetchForRole(currentUser.role, queryClient); } catch { /* ignore */ }
      try { preloadChunksForRole(currentUser.role); } catch { /* ignore */ }
    }
  }, [isLoading, currentUser?.role]);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async (retryCount = 0): Promise<void> => {
    // PTW-AUD-280: align the client SERVER_STARTING retry budget with the
    // server's 90 s readiness ceiling (45 × 2000 ms = 90 s) so a slow
    // post-deploy cold-wake does not exhaust the budget and bounce a valid PTW
    // session to /auth/expired. Generic transient failures (500 / network /
    // abort) keep the shorter budget.
    const SERVER_STARTING_MAX_RETRIES = 45;
    const TRANSIENT_MAX_RETRIES = 15;
    const RETRY_DELAY_MS = 2000;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    let shouldRetry = false;

    try {
      // RUM milestone: record when the auth check dispatches (retryCount=0 only
      // so we capture the cold-start dispatch time, not retry delays).
      if (retryCount === 0) {
        try { ((window as any).__nashidRum)?.recordAuthMeDispatch?.(); } catch { /* ignore */ }
      }
      const res = await fetch("/api/auth/me", {
        credentials: "include",
        signal: controller.signal,
      });
      // RUM milestone: auth/me response received (all attempts — first arrival wins).
      try { ((window as any).__nashidRum)?.recordAuthMeResponse?.(); } catch { /* ignore */ }
      if (res.ok) {
        setIsServerStarting(false);
        const data: ApiUserResponse = await res.json();
        const mapped = mapApiUser(data);
        setCurrentUser(mapped);
        setActiveTenant(data.activeTenant || null);
        setTenants(data.tenants || []);
        setCompanyPortals(data.companyPortals || []);
        setTenantFeatures(data.tenantFeatures || null);
        setTenantBranding(data.tenantBranding || null);
        // Task #3408 Step 4: seed the query cache from the auth response to
        // eliminate a redundant round-trip for any component using /api/auth/me.
        try { queryClient.setQueryData(["/api/auth/me"], data); } catch { /* ignore */ }
        // Task #3408 Step 2: persist warm-start hint for next page load.
        try { localStorage.setItem("nashid_sh", "1"); } catch { /* ignore */ }
        Sentry.setUser({
          id: mapped.id,
          email: mapped.email || undefined,
          username: mapped.name,
        });
        Sentry.setTag("tenantId", data.activeTenant?.id || "none");
        Sentry.setTag("tenantRole", data.tenantRole || "none");
      } else if (res.status === 503 && retryCount < SERVER_STARTING_MAX_RETRIES) {
        // Task #3402 — detect SERVER_STARTING specifically to surface the right
        // loading message.  Generic 503s (not SERVER_STARTING) still retry but
        // do not set isServerStarting.
        // PTW-AUD-280 — retry budget is now ≥90 s (SERVER_STARTING_MAX_RETRIES)
        // to match the server boot ceiling.
        try {
          const body = await res.clone().json();
          const code = body?.error?.code ?? body?.code;
          if (code === "SERVER_STARTING") {
            setIsServerStarting(true);
          }
        } catch {
          // body may not be JSON — ignore
        }
        shouldRetry = true;
      } else if (res.status === 503) {
        // PTW-AUD-280: the SERVER_STARTING retry budget (≥90 s) is exhausted but
        // the server is still returning 503 — it is still booting, NOT a session
        // expiry. Keep the warm-start hint and reload to restart the readiness
        // gate cleanly rather than dropping a valid PTW session to /auth/expired
        // or the login screen.
        setIsServerStarting(true);
        serverStartTimedOutRef.current = true;
        window.location.reload();
      } else if (res.status === 500 && retryCount < TRANSIENT_MAX_RETRIES) {
        // Task #3392 — retry on 500 (transient pool exhaustion).
        shouldRetry = true;
      } else {
        setIsServerStarting(false);
        // Task #3408 Step 2: clear warm-start hint on non-retryable failure
        // (session expired or auth rejected) so next visit shows cold start.
        // GAP-024: remove hint BEFORE any redirect so handle401Redirect correctly
        // classifies the next unauthenticated request as a first-time visitor.
        try { localStorage.removeItem("nashid_sh"); } catch { /* ignore */ }
        // GAP-024: when the initial page-load had a session hint (warm-start) and
        // the background /api/auth/me check returns a non-retryable failure, the
        // session has expired mid-load. Redirect immediately to /auth/expired so
        // the authenticated app shell is never painted before the navigation fires.
        if (wasWarmStartRef.current) {
          sessionExpiredDuringWarmStartRef.current = true;
          window.location.replace("/auth/expired");
          // fall through to finally — flag guards setIsLoading so no re-render fires
        }
        Sentry.setUser(null);
      }
    } catch (err) {
      // Task #3392 — also retry on AbortError (15s timeout from a saturated pool)
      // and on network errors, up to TRANSIENT_MAX_RETRIES, so a transient
      // connection blip does not permanently leave the client in the loading state.
      const isAbort = err instanceof DOMException && err.name === "AbortError";
      const isNetwork = err instanceof TypeError;
      if ((isAbort || isNetwork) && retryCount < TRANSIENT_MAX_RETRIES) {
        shouldRetry = true;
      }
    } finally {
      clearTimeout(timeoutId);
      // GAP-024: do not call setIsLoading when a warm-start session expiry is in
      // progress — we have already navigated away and a state update would
      // trigger a re-render of the app shell before the browser completes the
      // window.location.replace().
      // PTW-AUD-280: also skip the state flip when the server-start budget timed
      // out and a reload is pending — clearing isLoading here would briefly drop
      // a valid session to the login screen before the reload completes.
      if (!shouldRetry && !sessionExpiredDuringWarmStartRef.current && !serverStartTimedOutRef.current) {
        setIsServerStarting(false);
        setIsLoading(false);
        // PTW-018: the background session check has now resolved (success or a
        // non-retryable failure) — the warm-start verification window is closed.
        setSessionChecked(true);
      }
    }

    if (shouldRetry) {
      await new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      await checkSession(retryCount + 1);
    }
  };

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string; role?: string; hasCompletedOnboarding?: boolean; tenants?: TenantInfo[]; mfaRequired?: boolean; partnerPortalRedirect?: boolean; partnerIsUnassigned?: boolean; requiresPasswordChange?: boolean; userId?: string }> => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, deviceId: getOrCreateDeviceId() }),
      });
      if (!res.ok) {
        const data: ApiErrorResponse = await res.json();
        if (res.status === 403 && data.error?.code === "PASSWORD_CHANGE_REQUIRED") {
          return { success: false, requiresPasswordChange: true, userId: data.error.details?.userId };
        }
        return { success: false, error: data.error?.message || data.message || "Login failed" };
      }
      const data = await res.json();
      if (data.status === "mfa_required") {
        return { success: true, mfaRequired: true };
      }
      const apiData = data as ApiUserResponse & { partnerPortalRedirect?: boolean; partnerIsUnassigned?: boolean };
      const user = mapApiUser(apiData);
      setCurrentUser(user);
      Sentry.setUser({ id: user.id, email: user.email || undefined, username: user.name });

      // Task #3408 Step 4: use the login response directly instead of making a
      // second /api/auth/me round-trip. The login endpoint returns the full user
      // payload including activeTenant, tenants, features, and branding.
      // Fall back to a background /api/auth/me only if activeTenant is absent.
      if (apiData.activeTenant !== undefined || (apiData.tenants && apiData.tenants.length > 0)) {
        setActiveTenant(apiData.activeTenant || null);
        setTenants(apiData.tenants || []);
        setCompanyPortals(apiData.companyPortals || []);
        setTenantFeatures(apiData.tenantFeatures || null);
        setTenantBranding(apiData.tenantBranding || null);
        try { queryClient.setQueryData(["/api/auth/me"], apiData); } catch { /* ignore */ }
        try { localStorage.setItem("nashid_sh", "1"); } catch { /* ignore */ }
        return {
          success: true,
          role: user.role,
          hasCompletedOnboarding: apiData.hasCompletedOnboarding,
          tenants: apiData.tenants || [],
          partnerPortalRedirect: apiData.partnerPortalRedirect,
          partnerIsUnassigned: apiData.partnerIsUnassigned,
        };
      }

      // Fallback: login response didn't include tenant data — fetch it.
      const meController = new AbortController();
      const meTimeoutId = setTimeout(() => meController.abort(), 15000);
      try {
        const meRes = await fetch("/api/auth/me", {
          credentials: "include",
          signal: meController.signal,
        });
        if (meRes.ok) {
          const meData: ApiUserResponse = await meRes.json();
          setActiveTenant(meData.activeTenant || null);
          setTenants(meData.tenants || []);
          setCompanyPortals(meData.companyPortals || []);
          setTenantFeatures(meData.tenantFeatures || null);
          setTenantBranding(meData.tenantBranding || null);
          try { queryClient.setQueryData(["/api/auth/me"], meData); } catch { /* ignore */ }
          try { localStorage.setItem("nashid_sh", "1"); } catch { /* ignore */ }
          return {
            success: true,
            role: user.role,
            hasCompletedOnboarding: apiData.hasCompletedOnboarding,
            tenants: meData.tenants || [],
            partnerPortalRedirect: apiData.partnerPortalRedirect,
            partnerIsUnassigned: apiData.partnerIsUnassigned,
          };
        }
      } catch {
        // timeout or network error — fall through to the return below
      } finally {
        clearTimeout(meTimeoutId);
      }

      return {
        success: true,
        role: user.role,
        hasCompletedOnboarding: apiData.hasCompletedOnboarding,
        tenants: [],
        partnerPortalRedirect: apiData.partnerPortalRedirect,
        partnerIsUnassigned: apiData.partnerIsUnassigned,
      };
    } catch {
      return { success: false, error: "Network error" };
    }
  }, []);

  const logout = useCallback(async () => {
    // Clear all in-flight toasts immediately — before the network round-trip —
    // so User B never sees User A's notifications even on a slow/failed logout.
    dismissAllToasts();
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
    }
    // Task #3408 Step 2: clear warm-start hint so next visit shows cold start.
    try { localStorage.removeItem("nashid_sh"); } catch { /* ignore */ }
    // Reset auth-ready guard so next login flow can fire it again.
    authReadyFiredRef.current = false;
    Sentry.setUser(null);
    setCurrentUser(null);
    setActiveTenant(null);
    setTenants([]);
    setCompanyPortals([]);
    setTenantFeatures(null);
    setTenantBranding(null);
  }, []);

  const switchTenant = useCallback(async (tenantId: string, companyId?: string) => {
    setIsSwitchingTenant(true);
    try {
      const res = await authFetch("/api/auth/switch-tenant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, companyId }),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveTenant(data.activeTenant);
        const meRes = await fetch("/api/auth/me", { credentials: "include" });
        if (meRes.ok) {
          const meData: ApiUserResponse = await meRes.json();
          const mapped = mapApiUser(meData);
          setCurrentUser(mapped);
          setActiveTenant(meData.activeTenant || null);
          setTenants(meData.tenants || []);
          setCompanyPortals(meData.companyPortals || []);
          setTenantFeatures(meData.tenantFeatures || null);
          setTenantBranding(meData.tenantBranding || null);
          try { queryClient.setQueryData(["/api/auth/me"], meData); } catch { /* ignore */ }
          Sentry.setUser({ id: mapped.id, email: mapped.email || undefined, username: mapped.name });
          Sentry.setTag("tenantId", meData.activeTenant?.id || "none");
          Sentry.setTag("tenantRole", meData.tenantRole || "none");
        }
      }
    } catch {
    } finally {
      setIsSwitchingTenant(false);
    }
  }, []);

  const hasPermission = useCallback((permission: Permission) => {
    if (!currentUser) return false;
    // Prefer the canonical resolvedPermissions set (materialized by the 6-layer
    // engine on /api/auth/me: role → custom-role overrides → feature-flag strip).
    if (currentUser.resolvedPermissions && currentUser.resolvedPermissions.length > 0) {
      return currentUser.resolvedPermissions.includes(permission);
    }
    // Back-compat fallback for older /api/auth/me responses without resolvedPermissions.
    if (currentUser.permissions && currentUser.permissions.length > 0) {
      return currentUser.permissions.includes(permission);
    }
    const role = activeTenant?.role || currentUser.role;
    const userPermissions = (ROLE_PERMISSIONS as Record<string, Permission[]>)[role] || [];
    return userPermissions.includes(permission);
  }, [currentUser, activeTenant]);

  const hasRole = useCallback((requiredRole: UserRole | UserRole[]) => {
    if (!currentUser) return false;
    const role = activeTenant?.role || currentUser.role;
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(role as UserRole);
    }
    return role === requiredRole;
  }, [currentUser, activeTenant]);

  const refreshUsers = useCallback(async () => {
    try {
      const res = await authFetch("/api/users");
      if (res.ok) {
        const data: ApiUserResponse[] = await res.json();
        setTenantUsers(data.map(mapApiUser));
      }
    } catch {
    }
  }, []);

  const addUser = useCallback(async (userData: Omit<User, "id" | "joinedDate" | "status"> & { password?: string }) => {
    const res = await authFetch("/api/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: userData.name,
        email: userData.email,
        role: userData.role,
        password: userData.password,
      }),
    });
    if (!res.ok) {
      const data: ApiErrorResponse = await res.json();
      throw new Error(data.message || "Failed to add user");
    }
    await refreshUsers();
  }, [refreshUsers]);

  const updateUserRole = useCallback(async (userId: string, newRole: UserRole) => {
    const existingUser = tenantUsers.find(u => u.id === userId);
    const res = await authFetch(`/api/users/${userId}/role`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: newRole }),
    });
    if (!res.ok) {
      const data: ApiErrorResponse = await res.json();
      throw new Error(data.message || "Failed to update role");
    }
    if (existingUser) {
      const historyEntry: RoleHistory = {
        id: `h${Date.now()}`,
        userId: existingUser.id,
        userName: existingUser.name,
        oldRole: existingUser.role,
        newRole: newRole,
        changedBy: currentUser?.name || "System",
        date: new Date().toISOString()
      };
      setRoleHistory(prevHistory => [historyEntry, ...prevHistory]);
    }
    await refreshUsers();
  }, [refreshUsers, tenantUsers, currentUser]);

  const removeUser = useCallback(async (userId: string) => {
    const res = await authFetch(`/api/users/${userId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const data: ApiErrorResponse = await res.json();
      throw new Error(data.message || "Failed to remove user");
    }
    await refreshUsers();
  }, [refreshUsers]);

  return (
    <AuthContext.Provider value={{ 
      currentUser,
      isAuthenticated: !!currentUser,
      isLoading,
      isVerifyingSession: hasSessionHint && !sessionChecked && !currentUser,
      isServerStarting,
      isSwitchingTenant,
      hasPermission, 
      hasRole,
      tenantUsers,
      addUser,
      updateUserRole,
      removeUser,
      refreshUsers,
      roleHistory,
      login,
      logout,
      activeTenant,
      tenants,
      companyPortals,
      tenantFeatures,
      tenantBranding,
      switchTenant,
      checkSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
